import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { KnowledgeArticle } from '../../models/learn/knowledge-article';
import { KnowledgeBrowseItem } from '../../models/learn/knowledge-browse-item';
import { KnowledgeSearchResult } from '../../models/learn/knowledge-search-result';
import { KnowledgeAnswer, KnowledgeAnswerResult } from '../../models/learn/knowledge-answer';

/**
 * Reads knowledge articles from the .NET API content proxy (api/v1/Knowledge),
 * which fronts the Payload CMS and applies the tier gate. We attach the pitchIN
 * token only when the reader is logged in, so the proxy can tell members from
 * anonymous visitors; anonymous calls pass `noToken` to skip the auth/refresh path.
 */
@Injectable({ providedIn: 'root' })
export class KnowledgeService {
  private route = environment.apiServer + 'Api/v1/Knowledge/learn/';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  getArticle(slug: string): Observable<KnowledgeArticle> {
    return this.fetch(this.route, slug);
  }

  /**
   * Fetches a single published FAQ article via the proxy's faq/{slug} endpoint.
   * Same tier-gate + auth handling as getArticle() — only the collection differs.
   */
  getFaq(slug: string): Observable<KnowledgeArticle> {
    return this.fetch(environment.apiServer + 'Api/v1/Knowledge/faq/', slug);
  }

  /**
   * Builds the query params for a proxy call, tagging anonymous readers with `noToken` so the proxy
   * skips the auth/refresh path. Members keep their token (attached by the interceptor) so the tier
   * gate applies. Shared by every endpoint below — the one place the anonymous rule lives.
   */
  private params(extra: { [key: string]: string } = {}): { [key: string]: string } {
    const params = { ...extra };
    if (!this.authService.isAuthenticated()) {
      params['noToken'] = 'noToken';
    }
    return params;
  }

  private fetch(baseRoute: string, slug: string): Observable<KnowledgeArticle> {
    return this.http
      .get(baseRoute + encodeURIComponent(slug), { params: this.params() })
      .pipe(map((data: any) => data.result as KnowledgeArticle));
  }

  /**
   * Lists all published Knowledge Centre items for the browse lanes. Mirrors
   * getArticle()'s auth handling: anonymous callers pass `noToken` to skip the
   * auth/refresh path; members have their token attached as usual so the proxy
   * can apply the tier gate. We do NOT send an `audience` param — the full
   * published set is fetched and the hub filters by persona client-side, so
   * null-audience (general) items can show for both personas.
   */
  browse(): Observable<KnowledgeBrowseItem[]> {
    return this.http
      .get(environment.apiServer + 'Api/v1/Knowledge/browse', { params: this.params() })
      .pipe(map((data: any) => data.result as KnowledgeBrowseItem[]));
  }

  /**
   * One blended search across Learn + FAQ (#47). The proxy keyword-matches the published
   * corpus, applies the tier gate (internal never returned; members-only flagged `gated`),
   * and persona-ranks the result set. Mirrors browse()'s auth handling: anonymous callers
   * pass `noToken` to skip the auth/refresh path; members have their token attached so the
   * proxy can tell members from anonymous. `persona` biases the ranking to the reader's intent.
   */
  search(query: string, persona: 'investor' | 'issuer'): Observable<KnowledgeSearchResult[]> {
    return this.http
      .get(environment.apiServer + 'Api/v1/Knowledge/search', { params: this.params({ q: query, persona }) })
      .pipe(map((data: any) => (data.result as KnowledgeSearchResult[]) || []));
  }

  /**
   * Asks for a grounded AI answer to a reader question (#50). The proxy runs the money-sensitive
   * guardrail FIRST — a money/account/complaint question comes back `escalated` (no answer; a
   * `supportPath` to a human) — otherwise it grounds an extractive answer over the published corpus
   * (tier-gated: internal never served, members-only content never grounded for anonymous readers)
   * with inline citations and the persistent "educational, not financial advice" line. Every answer
   * is audited server-side; the returned `auditId` lets us attach a 👍/👎 rating afterwards. Mirrors
   * search()'s auth handling: anonymous callers pass `noToken`; members have their token attached so
   * the proxy gates the answer to the right tier. `persona` biases retrieval to the reader's intent.
   */
  answer(query: string, persona: 'investor' | 'issuer'): Observable<KnowledgeAnswerResult> {
    return this.http
      .get(environment.apiServer + 'Api/v1/Knowledge/answer', { params: this.params({ q: query, persona }) })
      .pipe(
        map((data: any) => ({
          answer: data.result as KnowledgeAnswer,
          auditId: (data.auditId as number) ?? 0,
        })),
      );
  }

  /**
   * Rates a previously-served AI answer (#50) by its audit id (👍/👎 + optional reason). The proxy
   * persists the rating via the tracked update path so it actually lands. Anonymous-safe — no reader
   * identity is sent; anonymous callers pass `noToken` to skip the auth/refresh path.
   */
  submitAnswerFeedback(
    auditId: number,
    helpful: boolean,
    reason?: string,
  ): Observable<unknown> {
    return this.http.post(
      environment.apiServer + 'Api/v1/Knowledge/answer/feedback',
      { auditId, helpful, reason: reason ?? null },
      { params: this.params() },
    );
  }

  /**
   * Submits a "Was this helpful?" vote (#48). The proxy persists it to Postgres (tracked insert),
   * so the vote actually lands. Anonymous-safe — no reader identity is sent; anonymous callers pass
   * `noToken` to skip the auth/refresh path. `collection` is learn|faq.
   */
  submitFeedback(
    slug: string,
    collection: 'learn' | 'faq',
    helpful: boolean,
  ): Observable<unknown> {
    return this.http.post(
      environment.apiServer + 'Api/v1/Knowledge/feedback',
      { slug, collection, helpful },
      { params: this.params() },
    );
  }
}
